<template>
  <div class="material-replace-form">
    <!-- Material Image and Info -->
    <div class="material-header">Выбранный материал:</div>
    <div class="material-card" v-if="selectedMaterial">
      <div class="material-image">
        <CustomImg
          class="textConstructor__photo"
          :src="materialPhotoUrl"
          :key="materialPhotoUrl"
          alt="material_photo"
        />
      </div>
      <div class="material-info">
        <h3>{{ selectedMaterial.name }}</h3>
        <p>Тип:<span>&nbsp;</span><span class="inner-text">{{ selectedMaterial.type }}</span></p>
        <p>ID материала:<span>&nbsp;</span><span class="inner-text">{{ selectedMaterial.id }}</span></p>
      </div>
    </div>

    <!-- Selection Steps -->
    <div class="selection-steps">
      <!-- WorkSet Type -->
      <div class="select-group">
        <CustomSelect
          label="Конструктив"
          :options="workSetOptions"
          optionValueKey="work_set_id"
          optionLabelKey="work_set_name"
          :value="selectedWorkSet"
          @change="(value) => onWorkSetSelect(value)"
          :clearable="false"
          :full-width="true"
        />
      </div>

      <!-- Room Selection -->
      <div class="select-group" v-if="selectedWorkSet">
        <CustomSelect
          label="Комната"
          :options="roomOptions"
          optionValueKey="room_id"
          optionLabelKey="room_name"
          :value="selectedRoom"
          @change="onRoomSelect"
          :clearable="false"
          :full-width="true"
        />
      </div>

      <!-- Set Selection -->
      <div class="select-group" v-if="selectedRoom">
        <CustomSelect
          label="Набор"
          :options="setOptions"
          optionValueKey="material_set_id"
          optionLabelKey="material_set_name"
          :value="selectedSet"
          @change="onSetSelect"
          :clearable="false"
          :full-width="true"
        />
      </div>
    </div>

    <!-- Replacement Material Section -->
    <div
      v-if="selectedSet && replacementMaterial.is_replace === 1"
      class="replacement-section"
    >
      <div class="material-header">Заменяемый материал:</div>

      <div class="material-card replacement">
        <div class="material-image">
          <CustomImg
            class="textConstructor__photo"
            :src="replacementMaterial.photo_url_old"
            alt="material_photo"
          />
        </div>
        <div class="material-info">
          <h3>{{ replacementMaterial.material_name_old }}</h3>
          <p>Тип:<span>&nbsp;</span><span class="inner-text">{{ replacementMaterial.material_type_name_old }}</span></p>
          <p>Конструктив:<span>&nbsp;</span><span class="inner-text">{{ replacementMaterial.work_set_name }}</span></p>
          <p>Комната:<span>&nbsp;</span><span class="inner-text">{{ replacementMaterial.room_name }}</span></p>
          <p>Набор:<span>&nbsp;</span><span class="inner-text">{{ replacementMaterial.material_set_name_old }}</span></p>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons">
      <q-btn
        :label="submitBtnLabel"
        color="secondary"
        class="replace-btn"
        :disabled="!selectedSet || !replacementMaterial"
      >
        <PopupConfirm
          :title="'Подтвердите действие'"
          :loading="replaceLoading"
          :approve="this.handleReplace"
        />
      </q-btn>
      <q-btn
        label="Отмена"
        color="red"
        flat
        class="cancel-btn"
        @click="handleCancel"
      />
    </div>
  </div>
</template>

<script>
import {
  parseQRData,
  getQRWorkSet,
  getQRRoom,
  getQRSet,
  getReplacementMaterial,
} from "@/views/ClientRequests/remontDS/dsCreationTemplates/changeTextConstructor/services";

import CustomImg from "@/components/common/CustomImg";
import CustomSelect from "@/components/form/CustomSelect";
import PopupConfirm from "@/components/common/PopupConfirm";

import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";

export default {
  name: "MaterialReplaceForm",
  components: {
    CustomImg,
    CustomSelect,
    PopupConfirm,
  },
  props: {
    QRData: {
      type: String,
      required: true,
    },
    client_request_id: {
      type: String,
      required: true,
    },
    replaceLoading: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      selectedWorkSet: null,
      selectedRoom: null,
      selectedSet: null,
      selectedMaterial: {},
      replacementMaterial: {},

      workSetOptions: [],
      roomOptions: [],
      setOptions: [],
    };
  },
  methods: {
    async getWorkSets() {
      const workSets = await getQRWorkSet(
        this.client_request_id,
        this.selectedMaterial.id
      );
      this.workSetOptions = workSets;
    },
    async getRooms() {
      const rooms = await getQRRoom(
        this.client_request_id,
        this.selectedMaterial.id,
        this.selectedWorkSet
      );
      this.roomOptions = rooms;
    },
    async getSets() {
      const sets = await getQRSet(
        this.client_request_id,
        this.selectedMaterial.id,
        this.selectedWorkSet,
        this.selectedRoom
      );
      this.setOptions = sets;
    },
    onWorkSetSelect(value) {
      this.selectedWorkSet = value;
      this.selectedRoom = null;
      this.selectedSet = null;
      this.getRooms();
    },
    onRoomSelect(value) {
      this.selectedRoom = value;
      this.selectedSet = null;
      this.getSets();
    },
    async onSetSelect(value) {
      this.selectedSet = value;
      const body = {
        material_id: this.selectedMaterial.id,
        work_set_id: this.selectedWorkSet,
        room_id: this.selectedRoom,
        material_set_id: this.selectedSet,
      };
      this.replacementMaterial = await getReplacementMaterial(
        this.client_request_id,
        body
      );
    },
    handleReplace() {
      this.$emit("replace", {
        client_material_id: this.replacementMaterial?.client_material_id,
        material_id: this.selectedMaterial.id,
        work_set_id: this.selectedWorkSet,
        room_id: this.selectedRoom,
        material_set_id: this.selectedSet,
      });
    },
    handleCancel() {
      this.$emit("cancel");
    },
    async initializeForm() {
      try {
        if (!this.QRData) {
          this.showErrorSnackbar("QR данные не найдены, попробуйте снова");
          this.$emit("scan-qr");
          return;
        }

        // Parse QR data and set initial material
        const parsedData = await parseQRData(
          this.client_request_id,
          this.QRData
        );
        if (parsedData) {
          this.selectedMaterial = {
            id: parsedData.material_id,
            name: parsedData.material_name,
            type: parsedData.material_type_name,
            photo_url: parsedData.photo_url,
          };
          this.getWorkSets();
        } else {
          this.showErrorSnackbar("QR данные не найдены, попробуйте снова");
          this.$emit("scan-qr");
        }
      } catch (error) {
        this.showErrorSnackbar(
          "Ошибка при парсинге QR данных, попробуйте снова"
        );
        this.$emit("scan-qr");
      }
    },
  },
  computed: {
    submitBtnLabel() {
      return this.replacementMaterial?.client_material_id
        ? "Заменить"
        : "Добавить";
    },
    materialPhotoUrl() {
      return this.selectedMaterial?.photo_url || "";
    },
  },
  async mounted() {
    if (this.QRData) {
      await this.initializeForm();
    }
  },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar };
  },
};
</script>

<style scoped lang="scss">
.material-replace-form {
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.material-card {
  display: flex;
  gap: 20px;
  padding: 15px;
  background: #f8f8f8;
  border-radius: 8px;
  margin-bottom: 20px;

  .material-image {
    width: 120px;
    height: 120px;
    background: #eee;
    border-radius: 4px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .material-info {
    flex: 1;

    h3 {
      margin: 0 0 8px 0;
      font-size: 16px;
      font-weight: 500;
      line-height: 1.2;
    }

    .inner-text {
      color: #666;
      font-size: 14px;
    }
    p {
      font-size: 14px;
      margin-bottom: 0;
    }
  }

  &.replacement {
    background: #f7f0f0;
  }
}

.selection-steps {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
}

.select-group {
  label {
    display: block;
    margin-bottom: 8px;
    color: #666;
    font-size: 14px;
  }
}

.custom-select {
  width: 100%;
}

.material-header {
  font-size: 16px;
  color: #666;
  font-weight: 400;
  margin-block: 10px;
}
.replacement-section {
  padding-top: 20px;
}
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;

  .replace-btn {
    background: #4caf50;
  }

  .cancel-btn {
    color: #f44336;
  }
}

// Responsive adjustments
@media (max-width: 480px) {
  .material-card {
    flex-direction: column;

    .material-image {
      width: 100%;
      height: 200px;
    }
  }
}
</style>
